<script setup lang="ts">
import { useOnboarding } from '@/composables/useOnboarding';
import { useUserStore } from '@/stores/useUserStore';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { computed } from 'vue';

interface Props {
  visible?: boolean;
  currentChapter?: string;
  completedChapters?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  currentChapter: undefined,
  completedChapters: () => [],
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  navigate: [chapterKey: string];
  close: [];
}>();

const dialogVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value),
});

const userStore = useUserStore();
const { isChapterAvailableForRole, getDisplayableChaptersForRole } = useOnboarding();

// Tous les chapitres possibles
const allChapters = [
  { key: '01_Introduction', title: 'Introduction', description: "Découverte de l'interface" },
  { key: '02_RecipeGeneration', title: 'Génération', description: "Créer des recettes avec l'IA" },
  { key: '03_RecipeDetails', title: 'Détails', description: 'Explorer les recettes créées' },
  { key: '04_RecipeActions', title: 'Actions', description: 'Modifier et sauvegarder' },
  { key: '05_Organization', title: 'Organisation', description: 'Classer dans des dossiers' },
  { key: '06_Scan', title: 'Scanner', description: 'Scanner des recettes existantes' },
  { key: '07_ProfileAndTeam', title: 'Profil', description: 'Configurer profil et équipe' },
  { key: '08_Planning', title: 'Planning', description: 'Planifier les menus' },
];

const chapters = computed(() => {
  const displayableKeys = getDisplayableChaptersForRole(userStore.contextualRole || 'admin');
  return allChapters.filter((chapter) => displayableKeys.includes(chapter.key));
});

function isCompleted(chapterKey: string): boolean {
  return props.completedChapters.includes(chapterKey);
}

function isCurrent(chapterKey: string): boolean {
  return props.currentChapter === chapterKey;
}

function isAvailable(chapterKey: string): boolean {
  // Vérifier si le chapitre est disponible pour le rôle actuel
  return isChapterAvailableForRole(chapterKey);
}

function navigateToChapter(chapterKey: string): void {
  // Vérifier la disponibilité avant de naviguer
  if (isChapterAvailableForRole(chapterKey)) {
    // Émettre d'abord l'événement de navigation
    emit('navigate', chapterKey);
    // Puis fermer le dialog (cela empêchera le @hide de déclencher l'événement close)
    emit('update:visible', false);
  }
}

function getChapterNumber(chapterKey: string): string {
  // Extraire le numéro du chapitre de la clé (ex: "01_Introduction" -> "1")
  const match = chapterKey.match(/^(\d+)_/);
  return match ? parseInt(match[1], 10).toString() : '';
}
</script>

<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :closable="true"
    :draggable="false"
    class="chapter-navigation-dialog"
    :style="{ width: '90vw', maxWidth: '900px' }"
    @hide="$emit('close')"
  >
    <template #header>
      <div class="flex items-center gap-3">
        <i class="pi pi-compass text-primary text-2xl"></i>
        <div>
          <h3 class="font-bold text-xl text-gray-900">Navigation des chapitres</h3>
          <p class="text-sm text-gray-600">Choisissez un chapitre à explorer</p>
        </div>
      </div>
    </template>

    <!-- Chapters Grid -->
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2">
      <div
        v-for="chapter in chapters"
        :key="chapter.key"
        class="group flex cursor-pointer items-center rounded-xl border-2 p-4 transition-all duration-200"
        :class="{
          'border-green-200 bg-green-50': isCompleted(chapter.key),
          'border-primary/30 bg-primary/5': isCurrent(chapter.key),
          'hover:border-primary/50 border-gray-200 bg-white hover:-translate-y-0.5 hover:shadow-md':
            isAvailable(chapter.key) && !isCompleted(chapter.key) && !isCurrent(chapter.key),
          'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60': !isAvailable(chapter.key),
        }"
        @click="navigateToChapter(chapter.key)"
      >
        <!-- Chapter Number -->
        <div
          class="mr-4 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl font-bold text-sm"
          :class="{
            'bg-green-500 text-white': isCompleted(chapter.key),
            'bg-primary text-white': isCurrent(chapter.key),
            'group-hover:bg-primary/10 group-hover:text-primary bg-gray-100 text-gray-600':
              isAvailable(chapter.key) && !isCompleted(chapter.key) && !isCurrent(chapter.key),
            'bg-gray-50 text-gray-400': !isAvailable(chapter.key),
          }"
        >
          {{ getChapterNumber(chapter.key) }}
        </div>

        <!-- Chapter Info -->
        <div class="min-w-0 flex-1">
          <h4 class="mb-1 truncate font-semibold text-sm text-gray-900">{{ chapter.title }}</h4>
          <p class="text-xs leading-tight text-gray-600">{{ chapter.description }}</p>
        </div>

        <!-- Status Icon -->
        <div class="ml-3 flex-shrink-0">
          <div
            class="flex h-8 w-8 items-center justify-center rounded-full font-bold text-xs"
            :class="{
              'bg-green-500 text-white': isCompleted(chapter.key),
              'bg-primary text-white': isCurrent(chapter.key),
              'bg-gray-200 text-gray-500': isAvailable(chapter.key) && !isCompleted(chapter.key) && !isCurrent(chapter.key),
              'bg-gray-100 text-gray-400': !isAvailable(chapter.key),
            }"
          >
            <i v-if="isCompleted(chapter.key)" class="pi pi-check"></i>
            <i v-else-if="isCurrent(chapter.key)" class="pi pi-play text-xs"></i>
            <i v-else-if="!isAvailable(chapter.key)" class="pi pi-lock text-xs"></i>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <Button label="Fermer" severity="secondary" @click="$emit('close')" />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
/* Styles personnalisés minimaux - le reste est géré par Tailwind */
.group:hover .group-hover\:bg-primary\/10 {
  background-color: rgb(var(--primary-500) / 0.1);
}

.group:hover .group-hover\:text-primary {
  color: rgb(var(--primary-500));
}
</style>
